import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button, Row, Col, Typography, Alert, Spin, Grid, Select } from 'antd';
import { SearchOutlined, ClearOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { fetchVehicleAnalysis, fetchVehicleVersions } from '../../services/api';
import { normalizeVehicleAnalysisResponse } from '../../utils/normalizeVehicleAnalysisResponse';
const { useBreakpoint } = Grid;
const { Text } = Typography;
const { Option } = Select;

const VINSearchForm = React.memo(function VINSearchForm({
  vin,
  setVin,
  version,
  setVersion,
  versions,
  loadingVersions,
  mileage,
  setMileage,
  loading,
  onSearch,
  onClear,
}) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: isMobile ? 20 : 16 }}>
        <Col xs={24}>
          <Input
            placeholder="Enter 17-character VIN (e.g., 3N1CN8EV4ML864286)"
            value={vin}
            onChange={(e) => {
              const value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
              if (value.length <= 17) setVin(value);
            }}
            onPressEnter={onSearch}
            style={{
              borderRadius: isMobile ? 16 : 12,
              height: isMobile ? 56 : 44,
              fontSize: isMobile ? 16 : 14,
              fontFamily: 'monospace',
              letterSpacing: 1,
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              padding: isMobile ? '0 20px' : '0 12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
            maxLength={17}
            disabled={loading}
          />
        </Col>
        <Col xs={24}>
          <Select
            placeholder="Select Version"
            value={version}
            onChange={setVersion}
            loading={loadingVersions}
            disabled={!vin || vin.length !== 17 || loading}
            allowClear
            style={{
              width: '100%',
            }}
            className="version-select"
          >
            {Array.isArray(versions) && versions.map((versionOption) => (
              <Option 
                key={versionOption} 
                value={versionOption}
              >
                {versionOption}
              </Option>
            ))}
          </Select>
          <style>
            {`
              .version-select .ant-select-selector {
                border-radius: ${isMobile ? '16px' : '12px'} !important;
                height: ${isMobile ? '56px' : '44px'} !important;
                background: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                color: #ffffff !important;
                font-size: ${isMobile ? '16px' : '14px'} !important;
                padding: 0 ${isMobile ? '20px' : '12px'} !important;
              }
              .version-select .ant-select-selection-item {
                color: #ffffff !important;
                line-height: ${isMobile ? '56px' : '44px'} !important;
              }
              .version-select .ant-select-selection-placeholder {
                color: rgba(255, 255, 255, 0.5) !important;
                line-height: ${isMobile ? '56px' : '44px'} !important;
              }
              .version-select .ant-select-arrow {
                color: rgba(255, 255, 255, 0.5) !important;
              }
              .version-select.ant-select-disabled .ant-select-selector {
                background: rgba(255, 255, 255, 0.02) !important;
                border-color: rgba(255, 255, 255, 0.05) !important;
                cursor: not-allowed !important;
              }
              .version-select .ant-select-dropdown {
                background: rgba(13, 15, 26, 0.95) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
              }
              .version-select .ant-select-item {
                color: #ffffff !important;
                background: transparent !important;
              }
              .version-select .ant-select-item-option-selected {
                background: rgba(59, 130, 246, 0.2) !important;
                color: #ffffff !important;
              }
              .version-select .ant-select-item-option:hover {
                background: rgba(255, 255, 255, 0.1) !important;
              }
            `}
          </style>
        </Col>
        <Col xs={24}>
          <div style={{
            position: 'relative',
            width: '100%',
            height: isMobile ? 56 : 44,
            borderRadius: isMobile ? 16 : 12,
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            padding: isMobile ? '0 16px' : '0 12px',
          }}>
            <Input
              placeholder="Mileage"
              value={mileage && !isNaN(parseInt(mileage, 10)) ? parseInt(mileage, 10).toLocaleString() : mileage}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setMileage(value);
              }}
              onPressEnter={onSearch}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#ffffff',
                fontSize: isMobile ? 16 : 14,
                height: '100%',
                flex: 1,
                padding: 0,
                boxShadow: 'none',
              }}
              disabled={loading}
            />
            <span style={{
              color: '#ffffff',
              fontSize: isMobile ? 16 : 14,
              marginLeft: 8,
              fontWeight: 500,
            }}>
              miles
            </span>
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={onSearch}
            loading={loading}
            disabled={!vin.trim() || !mileage.trim()}
            style={{
              height: isMobile ? 56 : 44,
              borderRadius: isMobile ? 16 : 12,
              fontSize: isMobile ? 16 : 14,
              fontWeight: 600,
              background: (!vin.trim() || !mileage.trim()) 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: (!vin.trim() || !mileage.trim()) 
                ? '1px solid rgba(255, 255, 255, 0.2)' 
                : 'none',
              color: (!vin.trim() || !mileage.trim()) 
                ? 'rgba(255, 255, 255, 0.4)' 
                : '#ffffff',
              cursor: (!vin.trim() || !mileage.trim()) 
                ? 'not-allowed' 
                : 'pointer',
              width: '100%',
              boxShadow: (!vin.trim() || !mileage.trim()) 
                ? 'none' 
                : '0 4px 16px rgba(59, 130, 246, 0.4)',
            }}
          >
            Search Insights
          </Button>
        </Col>
        <Col xs={24} sm={12}>
          <Button
            icon={<ClearOutlined />}
            onClick={onClear}
            disabled={loading}
            style={{
              height: isMobile ? 48 : 44,
              borderRadius: 12,
              fontSize: isMobile ? 16 : 14,
              fontWeight: 600,
              color: '#e4e6eb',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'transparent',
              width: '100%',
            }}
          >
            Clear
          </Button>
        </Col>
      </Row>
    </>
  );
});


const VINSearch = ({ onDataReceived }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // State
  const [searchVisible, setSearchVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // VIN Search state
  const [vin, setVin] = useState('');
  const [version, setVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [mileage, setMileage] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState({ year: null, make: null, model: null, trim: null });



  // Restore saved vehicle data on page refresh (only once on mount)
  useEffect(() => {
    const savedVehicleData = localStorage.getItem('hooptie_vehicle_data');
    if (savedVehicleData) {
      try {
        const parsedData = JSON.parse(savedVehicleData);
        // Extract vehicle info from saved data
        const vehicleData = parsedData.vehicle_info || parsedData.search_metadata || {};
        if (vehicleData.year || vehicleData.make || vehicleData.model || vehicleData.trim) {
          setVehicleInfo({
            year: vehicleData.year || null,
            make: vehicleData.make || null,
            model: vehicleData.model || null,
            trim: vehicleData.trim || null,
          });
        }
        onDataReceived?.(normalizeVehicleAnalysisResponse(parsedData));
        setSearchVisible(false);
      } catch (err) {
        console.error('Error parsing saved vehicle data:', err);
        localStorage.removeItem('hooptie_vehicle_data');
      }
    }
  }, []); // Empty dependency array - only run once on mount


  const validateVIN = (vinNumber) => /^[A-HJ-NPR-Z0-9]{17}$/.test(vinNumber.toUpperCase());

  const handleVINSearch = useCallback(async () => {
    if (!vin.trim()) {
      setError('Please enter a VIN number');
      return;
    }
    const cleanVIN = vin.trim().toUpperCase();
    if (!validateVIN(cleanVIN)) {
      setError('Please enter a valid 17-character VIN (no I, O, or Q allowed)');
      return;
    }

    if (!version) {
      setError('Please select a version');
      return;
    }

    if (!vehicleInfo.make || !vehicleInfo.model || !vehicleInfo.year) {
      setError('Vehicle information is incomplete. Please wait for version data to load.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build payload matching the API requirements
      const params = {
        make: vehicleInfo.make,
        mileage: mileage && !isNaN(parseInt(mileage)) ? parseInt(mileage, 10) : null,
        model: vehicleInfo.model,
        radius: 100,
        trim: vehicleInfo.trim || null,
        version: version,
        vin: cleanVIN,
        year: vehicleInfo.year,
      };

      // Remove null values from payload
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchVehicleAnalysis(params);
      if (response?.data) {
        const normalized = normalizeVehicleAnalysisResponse(response.data);
        localStorage.setItem('hooptie_vehicle_data', JSON.stringify(normalized));
        onDataReceived?.(normalized);
        setSearchVisible(false);
      } else {
        setError('No data found for this VIN');
      }
    } catch (err) {
      console.error('Error fetching vehicle analysis:', err);
      setError(err.response?.data?.message || err.message || 'Error fetching vehicle analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [vin, mileage, version, vehicleInfo, onDataReceived]);

  // Fetch versions when VIN is valid (17 characters)
  useEffect(() => {
    const fetchVersions = async () => {
      if (vin && vin.length === 17 && validateVIN(vin)) {
        setLoadingVersions(true);
        setVersion(null); // Reset version when VIN changes
        setVehicleInfo({ year: null, make: null, model: null, trim: null }); // Reset vehicle info
        try {
          const response = await fetchVehicleVersions(vin);
          let versionsData = [];
          
          if (response?.data) {
            // Extract vehicle_details (year, make, model, trim) from response
            // The API might return this in different formats
            let vehicleDetails = null;
            
            if (response.data.vehicle_details) {
              vehicleDetails = response.data.vehicle_details;
            } else if (response.data.vehicle_info) {
              vehicleDetails = response.data.vehicle_info;
            } else if (response.data.year || response.data.make || response.data.model) {
              vehicleDetails = response.data;
            }
            
            if (vehicleDetails) {
              setVehicleInfo({
                year: vehicleDetails.year || null,
                make: vehicleDetails.make || null,
                model: vehicleDetails.model || null,
                trim: vehicleDetails.trim || null,
              });
            }
            
            // Extract version items - try multiple possible response structures
            if (response.data.versions?.facets?.version && Array.isArray(response.data.versions.facets.version)) {
              versionsData = response.data.versions.facets.version.map(facet => facet.item);
            } else if (response.data.versions && Array.isArray(response.data.versions)) {
              versionsData = response.data.versions;
            } else if (response.data.data && Array.isArray(response.data.data)) {
              versionsData = response.data.data;
            } else if (Array.isArray(response.data)) {
              versionsData = response.data;
            } else if (typeof response.data === 'string') {
              versionsData = [response.data];
            }
          }
          
          setVersions(Array.isArray(versionsData) ? versionsData : []);
        } catch (err) {
          console.error('Error fetching vehicle versions:', err);
          setVersions([]);
          setError(err.response?.data?.message || 'Error fetching vehicle versions. Please try again.');
        } finally {
          setLoadingVersions(false);
        }
      } else {
        setVersions([]);
        setVersion(null);
        setVehicleInfo({ year: null, make: null, model: null, trim: null });
      }
    };

    fetchVersions();
  }, [vin]);



  const handleClear = () => {
    setVin('');
    setVersion(null);
    setVersions([]);
    setMileage('');
    setVehicleInfo({ year: null, make: null, model: null, trim: null });
    setError(null);
    setSearchVisible(true);
    localStorage.removeItem('hooptie_vehicle_data');
    onDataReceived?.(null);
  };



  return (
    <div style={{ marginBottom: isMobile ? 24 : 32 }}>
      {error && (
        <Alert 
          message={error} 
          type="error" 
          style={{ 
            marginBottom: 16, 
            borderRadius: 8,
            fontSize: isMobile ? 14 : 16
          }} 
          showIcon 
        />
      )}

      {/* Search container */}
      <div
        style={{
          marginBottom: isMobile ? 16 : 24,
          backgroundColor: 'rgba(13, 15, 26, 0.95)',
          borderRadius: isMobile ? 16 : 12,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
        }}
      >
        {/* Custom Header */}
        <div
          style={{
            padding: isMobile ? '20px 16px' : '16px 20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: searchVisible ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          }}
          onClick={() => setSearchVisible(!searchVisible)}
        >
          <span style={{ 
            color: '#e4e6eb', 
            fontSize: isMobile ? 18 : 16, 
            fontWeight: 600 
          }}>
            {searchVisible ? 'Hide Search' : 'Show Search'}
          </span>
          {searchVisible ? (
            <UpOutlined style={{ color: '#e4e6eb', fontSize: isMobile ? 18 : 16 }} />
          ) : (
            <DownOutlined style={{ color: '#e4e6eb', fontSize: isMobile ? 18 : 16 }} />
          )}
        </div>

        {/* Search Content */}
        {searchVisible && (
          <div style={{ 
            padding: isMobile ? '16px' : '20px', 
            backgroundColor: 'transparent' 
          }}>
            <VINSearchForm
              vin={vin}
              setVin={(v) => {
                setError(null);
                setVin(v);
              }}
              version={version}
              setVersion={setVersion}
              versions={versions}
              loadingVersions={loadingVersions}
              mileage={mileage}
              setMileage={(v) => {
                setError(null);
                setMileage(v);
              }}
              loading={loading}
              onSearch={handleVINSearch}
              onClear={handleClear}
            />
          </div>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Spin size="large" />
          <div style={{ marginTop: 8, color: '#a0a3b1' }}>Analyzing market data...</div>
        </div>
      )}
      
    </div>
  );
};

export default VINSearch;